import { useCallback, useEffect, useState, type FormEvent } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";
import { api } from "../api.js";
import { useSelectedTenant } from "../shell/tenant.js";
import { FieldReason, RefusalNotice, toRefusal, type Refusal } from "../zd/refusal.js";

// Z06 support log tab (S-063; spec 4.3 Z06, OPS-01, OPS-02, ENT-06, NFR-15, NFR-16). Lists the
// month's entries, each naming its tenant, with the support minutes per tenant, and adds an entry
// for the tenant picked in the tenant bar. Screens are paused (4 Nov 2026): plain components only,
// no styling; the tab moves onto the ZD application standard later. The server decides the rules.

export type SupportChannel = "phone" | "email";
export type SupportTopic = "onboarding" | "lite_page" | "crm" | "remembrance" | "billing" | "notifications" | "technical" | "privacy" | "other";

export interface SupportEntry {
  id: string;
  tenant: { id: string; name: string };
  recordedBy: { id: string; name: string };
  channel: SupportChannel;
  minutes: number;
  topic: SupportTopic;
  note: string | null;
  noteDeleted: boolean;
  createdAt: string;
}

export interface SupportLogAnswer {
  tenant: { id: string; name: string; status: string } | null;
  month: string;
  entries: SupportEntry[];
  totals: Array<{ tenant: { id: string; name: string }; minutes: number; entries: number }>;
  totalMinutes: number;
  limit: number;
  channels: SupportChannel[];
  topics: SupportTopic[];
  minutesMax: number;
  noteMax: number;
}

const CHANNEL: Record<SupportChannel, PhraseKey> = {
  phone: "console.support.channel_phone",
  email: "console.support.channel_email",
};

const TOPIC: Record<SupportTopic, PhraseKey> = {
  onboarding: "console.support.topic_onboarding",
  lite_page: "console.support.topic_lite_page",
  crm: "console.support.topic_crm",
  remembrance: "console.support.topic_remembrance",
  billing: "console.support.topic_billing",
  notifications: "console.support.topic_notifications",
  technical: "console.support.topic_technical",
  privacy: "console.support.topic_privacy",
  other: "console.support.topic_other",
};

const dateTime = new Intl.DateTimeFormat("en-ZA", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Johannesburg" });

/** A fresh request id for one entry (NFR-16): a second press sends the same id, so it counts once. */
export function newSupportRequestId(): string {
  return `z06-support-${crypto.randomUUID()}`;
}

interface Form {
  channel: SupportChannel | "";
  minutes: string;
  topic: SupportTopic | "";
  note: string;
}

const EMPTY: Form = { channel: "phone", minutes: "", topic: "", note: "" };

function AddEntry({ tenant, answer, onAdded }: { tenant: { id: string; name: string }; answer: SupportLogAnswer; onAdded: () => void }) {
  const [form, setForm] = useState<Form>(EMPTY);
  const [requestId, setRequestId] = useState(newSupportRequestId);
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    // A changed form is a new request.
    setRequestId(newSupportRequestId());
    setSaved(false);
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setSaved(false);
    const minutes = /^\d{1,6}$/.test(form.minutes) ? Number(form.minutes) : form.minutes;
    const r = await api<{ entry: SupportEntry }>("/v1/zd/support-log", {
      method: "POST",
      headers: { "Idempotency-Key": requestId },
      body: { tenantId: tenant.id, channel: form.channel, minutes, topic: form.topic, note: form.note },
    });
    setBusy(false);
    setRefusal(toRefusal(r));
    if (r.ok) {
      setSaved(true);
      setForm(EMPTY);
      setRequestId(newSupportRequestId());
      onAdded();
    }
  }

  return (
    <form onSubmit={submit} noValidate aria-label={t("console.support.add_title")}>
      <h3>{t("console.support.add_title")}</h3>
      <p>
        {t("console.support.tenant_label")} <strong>{tenant.name}</strong>
      </p>
      <RefusalNotice refusal={refusal} />
      {saved && <p role="status">{t("console.support.saved")}</p>}
      <fieldset aria-invalid={!!refusal?.fields.channel}>
        <legend>{t("console.support.channel_label")}</legend>
        {answer.channels.map((c) => (
          <label key={c}>
            <input type="radio" name="channel" value={c} checked={form.channel === c} onChange={() => set("channel", c)} />
            {t(CHANNEL[c])}
          </label>
        ))}
        <FieldReason refusal={refusal} field="channel" />
      </fieldset>
      <label htmlFor="support-minutes">{t("console.support.minutes_label")}</label>
      <input
        id="support-minutes"
        name="minutes"
        type="number"
        inputMode="numeric"
        min={1}
        max={answer.minutesMax}
        step={1}
        value={form.minutes}
        onChange={(e) => set("minutes", e.target.value)}
        aria-invalid={!!refusal?.fields.minutes}
        aria-describedby="support-minutes-hint"
      />
      <p className="hint" id="support-minutes-hint">
        {t("console.support.minutes_hint")}
      </p>
      <FieldReason refusal={refusal} field="minutes" />
      <label htmlFor="support-topic">{t("console.support.topic_label")}</label>
      <select id="support-topic" name="topic" value={form.topic} onChange={(e) => set("topic", e.target.value as SupportTopic | "")} aria-invalid={!!refusal?.fields.topic}>
        <option value="">{t("console.support.topic_choose")}</option>
        {answer.topics.map((x) => (
          <option key={x} value={x}>
            {TOPIC[x] ? t(TOPIC[x]) : x}
          </option>
        ))}
      </select>
      <FieldReason refusal={refusal} field="topic" />
      <label htmlFor="support-note">{t("console.support.note_label")}</label>
      <textarea
        id="support-note"
        name="note"
        maxLength={answer.noteMax}
        value={form.note}
        onChange={(e) => set("note", e.target.value)}
        aria-invalid={!!refusal?.fields.note}
        aria-describedby="support-note-hint"
      />
      <p className="hint" id="support-note-hint">
        {t("console.support.note_hint")}
      </p>
      <FieldReason refusal={refusal} field="note" />
      <FieldReason refusal={refusal} field="tenantId" />
      <button type="submit" disabled={busy}>
        {busy ? t("console.common.busy") : t("console.support.save")}
      </button>
    </form>
  );
}

export function SupportLog({ initialMonth }: { initialMonth?: string }) {
  const { selected } = useSelectedTenant();
  const tenantId = selected?.id ?? null;
  const [month, setMonth] = useState(initialMonth ?? "");
  const [answer, setAnswer] = useState<SupportLogAnswer | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (month) q.set("month", month);
    if (tenantId) q.set("tenant", tenantId);
    const r = await api<SupportLogAnswer>(`/v1/zd/support-log?${q.toString()}`);
    if (r.ok) {
      setAnswer(r.data);
      setLoadError(undefined);
    } else {
      setLoadError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("console.support.load_failed")));
    }
  }, [month, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section aria-labelledby="support-title">
      <h3 id="support-title">{t("console.support.title")}</h3>
      <p>{t("console.support.intro")}</p>
      <label>
        {t("console.support.month_label")}
        <input type="month" name="month" value={month || answer?.month || ""} onChange={(e) => setMonth(e.target.value)} />
      </label>
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {answer === undefined && !loadError && <p aria-busy="true">{t("console.common.loading")}</p>}
      {answer && (
        <>
          <p>
            {t("console.support.total_label")} <strong data-testid="support-total">{answer.totalMinutes}</strong>
          </p>
          {answer.totals.length > 0 && (
            <table aria-label={t("console.support.total_tenant_label")}>
              <thead>
                <tr>
                  <th scope="col">{t("console.support.column_tenant")}</th>
                  <th scope="col">{t("console.support.column_minutes")}</th>
                  <th scope="col">{t("console.support.entries_label")}</th>
                </tr>
              </thead>
              <tbody>
                {answer.totals.map((x) => (
                  <tr key={x.tenant.id}>
                    <td>{x.tenant.name}</td>
                    <td>{x.minutes}</td>
                    <td>{x.entries}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selected ? (
            <AddEntry key={selected.id} tenant={selected} answer={answer} onAdded={() => void load()} />
          ) : (
            <p className="hint">{t("console.support.choose_tenant")}</p>
          )}
          {answer.entries.length === 0 ? (
            <p className="empty">{t("console.support.empty")}</p>
          ) : (
            <table aria-label={t("console.support.entries_label")}>
              <thead>
                <tr>
                  <th scope="col">{t("console.support.column_when")}</th>
                  <th scope="col">{t("console.support.column_tenant")}</th>
                  <th scope="col">{t("console.support.column_by")}</th>
                  <th scope="col">{t("console.support.column_channel")}</th>
                  <th scope="col">{t("console.support.column_minutes")}</th>
                  <th scope="col">{t("console.support.column_topic")}</th>
                  <th scope="col">{t("console.support.column_note")}</th>
                </tr>
              </thead>
              <tbody>
                {answer.entries.map((e) => (
                  <tr key={e.id}>
                    <td>{dateTime.format(new Date(e.createdAt))}</td>
                    <td>{e.tenant.name}</td>
                    <td>{e.recordedBy.name}</td>
                    <td>{t(CHANNEL[e.channel])}</td>
                    <td>{e.minutes}</td>
                    <td>{TOPIC[e.topic] ? t(TOPIC[e.topic]) : e.topic}</td>
                    <td>{e.noteDeleted ? t("console.support.note_deleted") : (e.note ?? "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {answer.entries.length >= answer.limit && <p className="hint">{t("console.support.limit_reached")}</p>}
        </>
      )}
    </section>
  );
}
